import { useState, useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Play, Users, AlertCircle, AlertTriangle, Loader2, X,
  Activity, CheckCircle2, Clock, Calculator,
  ChevronUp, ChevronDown, ChevronsUpDown, Server,
} from 'lucide-react'
import { format, formatDistanceToNow, parseISO } from 'date-fns'
import {
  getAdminClients, runBilling, getSyncStatus, recalculateAllWarehouseFees, getRolloverSummary,
  getMtdCounts, repriceAllClients, getShippingByCarrier, getWorkerHealth,
} from '../../lib/api'
import RolloverBadge from '../../components/RolloverBadge'
import { useNavigate } from 'react-router-dom'
import { ToastContainer, useToast } from '../../components/Toast'
import ShippingByCourierTable from '../../components/ShippingByCourierTable'
import Tooltip from '../../components/Tooltip'

function formatCurrency(v) {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
  }).format(v ?? 0)
}

function timeAgo(iso) {
  if (!iso) return null
  try {
    return formatDistanceToNow(parseISO(iso), { addSuffix: true })
  } catch {
    return null
  }
}

// ── Clients Table ─────────────────────────────────────────────────────────────

const COL_HEADERS = [
  { key: 'name',                  label: 'Client',         align: 'left'  },
  { key: 'mtd_total',             label: 'MTD Total',      align: 'right' },
  { key: 'mtd_order_count',       label: 'Orders',         align: 'right' },
  { key: 'mtd_orders_fulfilment', label: 'Fulfilment',     align: 'right' },
  { key: 'mtd_orders_shipping',   label: 'Shipping',       align: 'right' },
  { key: 'mtd_sku_charges',       label: 'Order Details',  align: 'right' },
  { key: 'mtd_returns',           label: 'Returns',        align: 'right' },
  { key: 'mtd_exports',           label: 'Exports',        align: 'right' },
  { key: 'mtd_storage',           label: 'Storage',        align: 'right' },
  { key: 'mtd_activity',          label: 'Activity',       align: 'right' },
]

function SortIcon({ col, sortCol, sortDir }) {
  if (sortCol !== col) return <ChevronsUpDown size={11} className="text-slate-300 ml-1 inline-block" />
  return sortDir === 'asc'
    ? <ChevronUp size={11} className="text-blue-500 ml-1 inline-block" />
    : <ChevronDown size={11} className="text-blue-500 ml-1 inline-block" />
}

function ClientsTable({ clients, isLoading, error, totals, rolloverBy = {} }) {
  const navigate = useNavigate()
  const [sortCol, setSortCol] = useState('name')
  const [sortDir, setSortDir] = useState('asc')

  const totalOrders = clients.reduce((s, c) => s + (c.mtd_order_count ?? 0), 0)

  function handleSort(key) {
    if (sortCol === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortCol(key)
      setSortDir(key === 'name' ? 'asc' : 'desc')
    }
  }

  const sorted = useMemo(() => {
    return [...clients].sort((a, b) => {
      const av = a[sortCol] ?? (sortCol === 'name' ? '' : 0)
      const bv = b[sortCol] ?? (sortCol === 'name' ? '' : 0)
      let cmp = typeof av === 'string' ? av.localeCompare(bv) : av - bv
      return sortDir === 'asc' ? cmp : -cmp
    })
  }, [clients, sortCol, sortDir])

  if (isLoading) {
    return (
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {COL_HEADERS.map(h => (
                  <th key={h.key} className={`px-3 py-2 font-semibold text-slate-500 uppercase tracking-wider ${h.align === 'right' ? 'text-right' : 'text-left'}`}>
                    {h.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {Array.from({ length: 5 }).map((_, i) => (
                <tr key={i}>
                  {COL_HEADERS.map(h => (
                    <td key={h.key} className="px-3 py-2">
                      <div className="skeleton h-3 rounded" style={{ width: h.key === 'name' ? '120px' : '60px', marginLeft: h.align === 'right' ? 'auto' : undefined }} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  // Checked before the empty state: a failed fetch defaults to an empty list,
  // which would read as "No clients yet" on a platform that has clients.
  if (error) {
    return (
      <div className="card p-10 text-center">
        <AlertTriangle size={32} className="mx-auto text-red-500 mb-2" />
        <p className="text-sm font-medium text-slate-700">Couldn't load clients</p>
        <p className="text-xs text-slate-500 mt-1">
          This is <b>not</b> an empty client list — nothing above or below has been
          billed, counted or totalled from real data.
        </p>
        <p className="text-xs text-red-600 mt-2 font-mono break-all">
          {error?.response?.data?.detail || error?.message || 'Unknown error'}
        </p>
      </div>
    )
  }

  if (clients.length === 0) {
    return (
      <div className="card p-10 text-center">
        <Users size={32} className="mx-auto text-slate-300 mb-2" />
        <p className="text-slate-400 text-sm">No clients yet</p>
      </div>
    )
  }

  return (
    <div className="card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              {COL_HEADERS.map(h => (
                <th
                  key={h.key}
                  onClick={() => handleSort(h.key)}
                  className={`px-3 py-2 font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap select-none cursor-pointer hover:text-slate-700 hover:bg-slate-100 transition-colors ${h.align === 'right' ? 'text-right' : 'text-left'}`}
                >
                  {h.label}
                  <SortIcon col={h.key} sortCol={sortCol} sortDir={sortDir} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {sorted.map((c) => {
              const isOverdue = c.overdue
              return (
                <tr
                  key={c.id}
                  onClick={() => navigate(`/admin/clients/${c.id}`)}
                  className={`cursor-pointer hover:bg-blue-50/40 transition-colors ${isOverdue ? 'bg-red-50/30' : ''}`}
                >
                  <td className="px-3 py-2 font-medium text-slate-800 whitespace-nowrap">
                    <span className="hover:text-blue-600 transition-colors">{c.name}</span>
                    <span className="ml-2 align-middle inline-flex">
                      <RolloverBadge amount={rolloverBy[c.id]?.total} items={rolloverBy[c.id]?.items} />
                    </span>
                    {isOverdue && (
                      <span className="ml-2 inline-flex items-center gap-0.5 text-red-600 bg-red-100 rounded-full px-1.5 py-0.5 text-[10px] font-semibold">
                        <AlertCircle size={9} />
                        Overdue
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-800">
                    {formatCurrency(c.mtd_total)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                    {c.mtd_order_count ?? 0}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-600">
                    {formatCurrency(c.mtd_orders_fulfilment)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-600">
                    {formatCurrency(c.mtd_orders_shipping)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-600">
                    {formatCurrency(c.mtd_sku_charges)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-600">
                    {formatCurrency(c.mtd_returns)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-600">
                    {formatCurrency(c.mtd_exports)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-600">
                    {formatCurrency(c.mtd_storage)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-600">
                    {formatCurrency(c.mtd_activity)}
                  </td>
                </tr>
              )
            })}
          </tbody>
          <tfoot className="bg-slate-50 border-t-2 border-slate-200">
            <tr>
              <td className="px-3 py-2 font-semibold text-slate-500 uppercase tracking-wider">
                Total ({clients.length} clients)
              </td>
              <td className="px-3 py-2 text-right tabular-nums font-bold text-slate-800">
                {formatCurrency(totals.mtd_total)}
              </td>
              <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-700">
                {totalOrders}
              </td>
              <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-700">
                {formatCurrency(totals.mtd_orders_fulfilment)}
              </td>
              <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-700">
                {formatCurrency(totals.mtd_orders_shipping)}
              </td>
              <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-700">
                {formatCurrency(totals.mtd_sku_charges)}
              </td>
              <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-700">
                {formatCurrency(totals.mtd_returns)}
              </td>
              <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-700">
                {formatCurrency(totals.mtd_exports)}
              </td>
              <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-700">
                {formatCurrency(totals.mtd_storage)}
              </td>
              <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-700">
                {formatCurrency(totals.mtd_activity)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  )
}

// ── Billing Modal ─────────────────────────────────────────────────────────────

function BillingModal({ onClose, onConfirm, loading }) {
  const now = new Date()
  const [year, setYear]   = useState(now.getFullYear())
  const [month, setMonth] = useState(now.getMonth() + 1)

  const months = [
    'January','February','March','April','May','June',
    'July','August','September','October','November','December',
  ]

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-200">
          <h3 className="font-semibold text-slate-800">Run Billing</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
            <X size={18} />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-slate-600">
            Select the billing period to process. This will generate Xero invoices for all clients.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Year</label>
              <input
                type="number" value={year} min={2020} max={2099}
                onChange={(e) => setYear(Number(e.target.value))}
                className="input"
              />
            </div>
            <div>
              <label className="label">Month</label>
              <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className="input">
                {months.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
              </select>
            </div>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
            This action will create draft invoices in Xero for {months[month - 1]} {year}.
            Please confirm before proceeding.
          </div>
        </div>
        <div className="px-6 py-4 border-t border-slate-200 flex gap-3 justify-end">
          <button onClick={onClose} className="btn-secondary" disabled={loading}>Cancel</button>
          <button onClick={() => onConfirm(year, month)} disabled={loading} className="btn-primary">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
            {loading ? 'Running…' : 'Run Billing'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Sync / Data Management Card ───────────────────────────────────────────────

function BreakdownRow({ color, label, value, valueClass = 'text-slate-700', hint }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-2 min-w-0">
        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${color}`} />
        <span className="text-xs text-slate-500">{label}</span>
        {hint && <span className="text-[10px] text-slate-400">· {hint}</span>}
      </div>
      <span className={`text-sm tabular-nums ${valueClass}`}>{value}</span>
    </div>
  )
}

function SyncCard({ icon, title, description, lastSync, busy, actions }) {
  return (
    <div className="card p-4 flex flex-col gap-3">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-800">{title}</p>
          <p className="text-xs text-slate-400 mt-0.5">{description}</p>
        </div>
      </div>

      {(busy || lastSync) && (
        <div className="flex items-center gap-1.5 text-xs text-slate-400">
          {busy
            ? <>
                <Loader2 size={11} className="animate-spin" />
                <span>Syncing now…</span>
              </>
            : <>
                <Clock size={11} />
                <span>Last sync {lastSync}</span>
              </>}
        </div>
      )}

      <div className="flex flex-wrap gap-2 pt-1 border-t border-slate-100">
        {actions.map((action, i) => (
          <Tooltip key={i} text={action.tooltip}>
            <button
              onClick={action.onClick}
              disabled={busy || action.disabled}
              className={action.primary ? 'btn-primary text-xs py-1.5 px-3' : 'btn-secondary text-xs py-1.5 px-3'}
            >
              {(busy && action.spinning) || action.loading
                ? <Loader2 size={12} className="animate-spin" />
                : action.icon}
              {action.label}
            </button>
          </Tooltip>
        ))}
      </div>
    </div>
  )
}

// ── Background Sync Health Card ───────────────────────────────────────────────

const WORKER_STATUS_META = {
  ok:        { dot: 'bg-emerald-500',            text: 'text-emerald-700', label: 'OK' },
  running:   { dot: 'bg-blue-500 animate-pulse', text: 'text-blue-700',    label: 'Running' },
  stale:     { dot: 'bg-amber-500',              text: 'text-amber-700',   label: 'Stale' },
  error:     { dot: 'bg-red-500',                text: 'text-red-700',     label: 'Error' },
  never_run: { dot: 'bg-slate-300',              text: 'text-slate-400',   label: 'No data' },
}

function WorkerHealthCard({ health, error }) {
  const jobs = health?.jobs ?? []
  const tableMissing = health && health.table_present === false
  const errored  = jobs.filter(j => j.status === 'error').length
  const problems = jobs.filter(j => j.status !== 'ok' && j.status !== 'running').length
  // The `error` case comes first: an unread health response leaves `jobs` empty,
  // which would otherwise report the reassuring-looking "Waiting for data".
  const overall = error
    ? { text: 'text-red-700', label: 'Health unknown' }
    : jobs.length === 0
    ? { text: 'text-slate-400', label: 'Waiting for data' }
    : errored > 0
      ? { text: 'text-red-700', label: `${errored} job${errored !== 1 ? 's' : ''} failing` }
      : problems === 0
        ? { text: 'text-emerald-700', label: 'All systems live' }
        : { text: 'text-amber-700', label: `${problems} job${problems !== 1 ? 's' : ''} need attention` }

  return (
    <div className="card p-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
            <Server size={16} className="text-slate-600" />
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-800">Background Sync Health</p>
            <p className="text-xs text-slate-400 mt-0.5">Data syncs automatically every few minutes — no manual sync needed</p>
          </div>
        </div>
        <span className={`text-xs font-medium flex-shrink-0 ${overall.text}`}>{overall.label}</span>
      </div>

      {error ? (
        <div className="flex items-start gap-2">
          <AlertTriangle size={14} className="text-red-500 flex-shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-xs font-medium text-slate-700">
              Couldn't read worker health. This is <b>not</b> "all systems live" — the
              background syncs may be stalled or failing with nothing shown here.
            </p>
            <p className="text-xs text-red-600 mt-0.5 font-mono break-all">
              {error?.response?.data?.detail || error?.message || 'Unknown error'}
            </p>
          </div>
        </div>
      ) : tableMissing ? (
        <p className="text-xs text-amber-600">worker_runs table not found — run the 2026-05-17-worker-runs migration.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-2.5">
          {jobs.map(j => {
            const m = WORKER_STATUS_META[j.status] ?? WORKER_STATUS_META.never_run
            return (
              <div key={j.name} className="flex items-center justify-between gap-2 min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${m.dot}`} />
                  <span className="text-sm text-slate-700 truncate">{j.label}</span>
                </div>
                <span className={`text-xs flex-shrink-0 ${m.text}`} title={j.error_message || ''}>
                  {j.status === 'error' || j.status === 'stale'
                    ? m.label
                    : (j.last_run ? timeAgo(j.last_run) : m.label)}
                </span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── Main Dashboard ────────────────────────────────────────────────────────────

export default function AdminDashboard() {
  const queryClient = useQueryClient()
  const { toasts, addToast, removeToast } = useToast()

  // Reprice — busy is local; "last reprice" is persisted in sync_meta and
  // exposed via syncStatus.reprice. A local-state fallback covers the small
  // window between mutation success and the next sync_status refetch.
  const [repricing, setRepricing] = useState(false)
  const [repricedAt, setRepricedAt] = useState(null)

  // Recalculate warehouse activity fees
  const [recalculating, setRecalculating] = useState(false)

  // Billing modal
  const [billingModal,   setBillingModal]   = useState(false)
  const [billingLoading, setBillingLoading] = useState(false)

  // `clientsError` is destructured deliberately. Every figure on this page is
  // summed from `clients`, so with only `data: clients = []` a failed fetch
  // renders MTD Revenue $0.00, Billing Total $0.00 and "No clients yet" — a
  // dead-quiet month rather than a broken request.
  const { data: clients = [], isLoading, error: clientsError } = useQuery({
    queryKey: ['admin', 'clients'],
    queryFn: getAdminClients,
  })

  // Prior-period charges queued for each client's next invoice — drives the
  // small "R" marker next to the client name (not part of MTD).
  const { data: rolloverSummary } = useQuery({
    queryKey: ['admin', 'rollover-summary'],
    queryFn: () => getRolloverSummary(),
    staleTime: 300_000,
    refetchInterval: false,        // opt out of the global 60s poll — this is a
    refetchOnWindowFocus: false,   // multi-table scan, not worth re-running often
  })
  const rolloverBy = Object.fromEntries(
    (rolloverSummary?.clients || []).map(r => [r.client_id, r])
  )

  // Current-MTD shipping-by-courier breakdown — no period arg so the
  // backend mirrors the same date window list_clients uses.
  const {
    data: shippingByCarrier = [],
    isLoading: shippingByCarrierLoading,
    error: shippingByCarrierError,
  } = useQuery({
    queryKey: ['admin', 'shipping-by-carrier', 'mtd'],
    queryFn: () => getShippingByCarrier(),
  })

  const { data: syncStatus } = useQuery({
    queryKey: ['sync', 'status'],
    queryFn: getSyncStatus,
    refetchInterval: 60_000,
  })

  // A failed health read must not look like "no data yet" — an unreadable
  // health panel is exactly how a stalled worker goes unnoticed.
  const { data: workerHealth, error: workerHealthError } = useQuery({
    queryKey: ['sync', 'worker-health'],
    queryFn: getWorkerHealth,
    refetchInterval: 60_000,
  })

  const { data: mtdCounts } = useQuery({
    queryKey: ['admin', 'mtd-counts'],
    queryFn: () => getMtdCounts(),
    refetchInterval: 60_000,
  })

  // Summary stats
  const totalClients     = clients.length
  const totalMtd         = clients.reduce((s, c) => s + (c.mtd_total             ?? 0), 0)
  const totalFulfilment  = clients.reduce((s, c) => s + (c.mtd_orders_fulfilment ?? 0), 0)
  const totalShipping    = clients.reduce((s, c) => s + (c.mtd_orders_shipping   ?? 0), 0)
  const totalStorage     = clients.reduce((s, c) => s + (c.mtd_storage           ?? 0), 0)
  const totalActivity    = clients.reduce((s, c) => s + (c.mtd_activity          ?? 0), 0)
  const totalSkuCharges  = clients.reduce((s, c) => s + (c.mtd_sku_charges       ?? 0), 0)
  const totalReturns     = clients.reduce((s, c) => s + (c.mtd_returns  ?? 0), 0)
  const totalExports     = clients.reduce((s, c) => s + (c.mtd_exports  ?? 0), 0)
  const totalOutstanding = clients.reduce((s, c) => s + (c.outstanding         ?? 0), 0)
  const totalXeroBilled  = clients.reduce((s, c) => s + (c.xero_total          ?? 0), 0)
  const totalXeroPaid    = clients.reduce((s, c) => s + (c.xero_paid           ?? 0), 0)
  const totalOverdueAmt  = clients.reduce((s, c) => s + (c.xero_overdue_amount ?? 0), 0)
  // Due is scoped to last month so the card is internally consistent:
  // Total = Paid + Due. `totalOutstanding` is all-time AR (surfaced
  // separately as its own row below) and would otherwise leak stale
  // pre-last-month balances into this breakdown.
  const totalDueAmt      = Math.max(0, totalXeroBilled - totalXeroPaid)
  const overdueClients   = clients.filter((c) => c.overdue)
  // Last-month label (e.g. "April 2026")
  const lastMonthLabel   = useMemo(() => {
    const d = new Date()
    d.setDate(1)
    d.setMonth(d.getMonth() - 1)
    return d.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' })
  }, [])
  // "Last sync" for the Xero invoice/payment data — the most recent
  // xero_last_check across all clients. Falls back to syncStatus.xero
  // (sync_meta.last_xero_sync) if no client has been checked yet.
  const lastXeroCheck    = useMemo(() => {
    const stamps = clients.map(c => c.xero_last_check).filter(Boolean)
    if (!stamps.length) return syncStatus?.xero ?? null
    return stamps.sort().slice(-1)[0]
  }, [clients, syncStatus])

  // ── Handlers ────────────────────────────────────────────────────────────────

  async function handleRecalculateAll() {
    setRecalculating(true)
    try {
      const result = await recalculateAllWarehouseFees()
      addToast({
        type: 'success',
        message: `Fees recalculated — ${result.recalculated} activit${result.recalculated === 1 ? 'y' : 'ies'} across ${result.clients} client${result.clients !== 1 ? 's' : ''}.`
          + (result.skipped_locked ? ` ${result.skipped_locked} left untouched in already-invoiced months.` : ''),
        duration: 6000,
      })
      queryClient.invalidateQueries({ queryKey: ['admin'] })
      queryClient.invalidateQueries({ queryKey: ['sync', 'status'] })
    } catch (err) {
      addToast({ type: 'error', message: `Recalculation failed: ${err.response?.data?.detail || err.message}` })
    } finally {
      setRecalculating(false)
    }
  }

  async function handleRepriceAll() {
    setRepricing(true)
    try {
      const result = await repriceAllClients()
      addToast({
        type: 'success',
        message: `Repriced ${result.orders_repriced} order${result.orders_repriced === 1 ? '' : 's'} across ${result.clients_processed} client${result.clients_processed === 1 ? '' : 's'}.`,
        duration: 6000,
      })
      setRepricedAt(new Date().toISOString())
      queryClient.invalidateQueries({ queryKey: ['admin'] })
      queryClient.invalidateQueries({ queryKey: ['sync', 'status'] })
    } catch (err) {
      addToast({ type: 'error', message: `Reprice failed: ${err.response?.data?.detail || err.message}` })
    } finally {
      setRepricing(false)
    }
  }

  async function handleRunBilling(year, month) {
    setBillingLoading(true)
    try {
      const result = await runBilling(year, month)
      addToast({
        type: 'success',
        message: `Billing run complete for ${month}/${year}. ${result.invoices_created ?? ''} invoices created.`,
        duration: 6000,
      })
      setBillingModal(false)
      queryClient.invalidateQueries({ queryKey: ['admin'] })
    } catch (err) {
      addToast({ type: 'error', message: `Billing failed: ${err.response?.data?.message || err.message}` })
    } finally {
      setBillingLoading(false)
    }
  }

  return (
    <>
      <div className="space-y-6">

        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold text-slate-800">Admin Overview</h2>
            <p className="text-sm text-slate-500 mt-0.5">OPS 3PL billing management</p>
          </div>
          <button onClick={() => setBillingModal(true)} className="btn-primary">
            <Play size={15} />
            Run Billing
          </button>
        </div>

        {/* Every stat below is summed from `clients`. If that fetch failed the
            sums are all zero, so say so loudly rather than report a $0 month. */}
        {clientsError && (
          <div className="card border-red-200 bg-red-50 p-4">
            <p className="text-sm font-semibold text-red-800 flex items-center gap-1.5">
              <AlertTriangle size={15} /> Couldn't load client billing data
            </p>
            <p className="text-sm text-red-700 mt-1">
              The figures on this page are <b>not</b> a real $0 month — nothing could be
              read, so treat every total, count and breakdown below as unknown.
            </p>
            <p className="text-xs text-red-600 mt-1.5 font-mono break-all">
              {clientsError?.response?.data?.detail || clientsError?.message || 'Unknown error'}
            </p>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">

          {/* MTD Revenue + client count */}
          <div className="card p-4">
            <div className="flex items-start justify-between mb-1">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">MTD Revenue</p>
              <span className="flex items-center gap-1 text-xs font-medium text-blue-600 bg-blue-50 rounded-full px-2 py-0.5">
                <Users size={11} />
                {isLoading || clientsError ? '—' : totalClients}
              </span>
            </div>
            <p className="text-2xl font-bold text-slate-800 mb-2">{isLoading || clientsError ? '—' : formatCurrency(totalMtd)}</p>
            <div className="flex flex-col gap-1">
              {[
                ['Order Fulfilment', totalFulfilment],
                ['Order Shipping',   totalShipping],
                ['Order Details',    totalSkuCharges],
                ['Returns',          totalReturns],
                ['Exports',          totalExports],
                ['Storage',          totalStorage],
                ['Activity',         totalActivity],
              ].map(([label, val]) => (
                <div key={label} className="flex justify-between items-center">
                  <span className="text-xs text-slate-400">{label}</span>
                  <span className="text-xs font-medium text-slate-600 tabular-nums">{clientsError ? '—' : formatCurrency(val)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* MTD Numbers */}
          <div className="card p-4">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">MTD Numbers</p>
            <div className="flex flex-col gap-1.5">
              {[
                { label: 'Orders',         value: mtdCounts?.total_orders  ?? '—' },
                { label: '+Items',         value: mtdCounts?.add_items      ?? '—' },
                { label: 'Inserts',        value: mtdCounts?.std_inserts    ?? '—' },
                { label: 'Custom Inserts', value: mtdCounts?.custom_inserts ?? '—' },
                { label: 'No Insert',      value: mtdCounts?.no_inserts     ?? '—' },
                { label: 'Case Picks',     value: mtdCounts?.case_picks     ?? '—' },
              ].map(({ label, value }) => (
                <div key={label} className="flex justify-between items-center">
                  <span className="text-xs text-slate-500">{label}</span>
                  <span className="text-sm font-semibold text-slate-800 tabular-nums">{value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Billing Total + Paid / Due / Overdue breakdown */}
          <div className="card p-4 flex flex-col gap-3 relative">
            {/* Last sync — small, top-right corner */}
            <p
              className="absolute top-3 right-4 text-[10px] text-slate-400"
              title={lastXeroCheck
                ? `Xero invoice & payment data last synced ${format(parseISO(lastXeroCheck), 'dd MMM yyyy HH:mm')}`
                : 'Click "Sync from Xero" on the Invoice Status card to populate.'}
            >
              {lastXeroCheck
                ? `Last sync ${format(parseISO(lastXeroCheck), 'dd MMM HH:mm')}`
                : 'Never synced'}
            </p>

            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Billing Total</p>
              <p className="text-2xl font-bold text-slate-800">
                {isLoading || clientsError ? '—' : formatCurrency(totalXeroBilled)}
              </p>
              <p className="text-[11px] text-slate-400 mt-0.5">{lastMonthLabel} invoices</p>
            </div>

            <div className="border-t border-slate-100 pt-3 space-y-2">
              <BreakdownRow
                color="bg-emerald-500"
                label="Paid"
                value={isLoading || clientsError ? '—' : formatCurrency(totalXeroPaid)}
                valueClass="text-emerald-700"
              />
              <BreakdownRow
                color="bg-slate-400"
                label="Due"
                value={isLoading || clientsError ? '—' : formatCurrency(totalDueAmt)}
                valueClass="text-slate-700"
                hint="last-month"
              />
              <BreakdownRow
                color={totalOverdueAmt > 0 ? 'bg-red-500' : 'bg-slate-200'}
                label="Overdue"
                value={isLoading || clientsError ? '—' : formatCurrency(totalOverdueAmt)}
                valueClass={totalOverdueAmt > 0 ? 'text-red-600 font-semibold' : 'text-slate-400'}
                hint={overdueClients.length > 0
                  ? `${overdueClients.length} client${overdueClients.length !== 1 ? 's' : ''} · all-time`
                  : 'all-time'}
              />
              <BreakdownRow
                color="bg-slate-300"
                label="All-time AR"
                value={isLoading || clientsError ? '—' : formatCurrency(totalOutstanding)}
                valueClass="text-slate-500"
                hint="all-time"
              />
            </div>
          </div>

        </div>

        {/* Data Management — automatic background syncs + manual billing actions */}
        <div>
          <h3 className="font-semibold text-slate-800 mb-3">Data Management</h3>

          <WorkerHealthCard health={workerHealth} error={workerHealthError} />

          {/* Manual billing recomputes — not background-synced, run on demand */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
            <SyncCard
              icon={<Calculator size={16} className="text-indigo-600" />}
              title="Reprice Orders"
              description="Re-stamp current-month orders with live rate-card fees"
              lastSync={timeAgo(
                repricedAt && (!syncStatus?.reprice || repricedAt > syncStatus.reprice)
                  ? repricedAt
                  : syncStatus?.reprice
              )}
              busy={repricing}
              actions={[
                {
                  label: repricing ? 'Repricing…' : 'Reprice All',
                  onClick: handleRepriceAll,
                  icon: <Calculator size={12} />,
                  loading: repricing,
                  disabled: repricing,
                  tooltip: 'Recalculate current-month order fees for all active clients against their current pricing tiers. Use after tier crossovers, rate changes, or carton-size updates.',
                },
              ]}
            />

            <SyncCard
              icon={<CheckCircle2 size={16} className="text-purple-600" />}
              title="Recalculate Activity Fees"
              description="Recompute warehouse activity fees at current rates"
              busy={recalculating}
              actions={[
                {
                  label: recalculating ? 'Recalculating…' : 'Recalculate',
                  onClick: handleRecalculateAll,
                  icon: <CheckCircle2 size={12} />,
                  loading: recalculating,
                  disabled: recalculating,
                  tooltip: 'Recompute warehouse activity fees for every client using their current rates. Run after updating rates to correct any $0 fees.',
                },
              ]}
            />
          </div>
        </div>

        {/* All clients table */}
        <div>
          <h3 className="font-semibold text-slate-800 mb-3">All Clients</h3>
          <ClientsTable
            clients={clients}
            isLoading={isLoading}
            error={clientsError}
            rolloverBy={rolloverBy}
            totals={{
              mtd_total: totalMtd,
              mtd_orders_fulfilment: totalFulfilment,
              mtd_orders_shipping: totalShipping,
              mtd_sku_charges: totalSkuCharges,
              mtd_returns: totalReturns,
              mtd_exports: totalExports,
              mtd_storage: totalStorage,
              mtd_activity: totalActivity,
            }}
          />
        </div>

        {/* Shipping by Courier — current MTD breakdown across all clients.
            Handled here rather than in the table: its empty state reads
            "No shipped orders", which a failed fetch would borrow. */}
        {shippingByCarrierError ? (
          <div className="card p-10 text-center">
            <AlertTriangle size={28} className="mx-auto text-red-500 mb-2" />
            <p className="text-sm font-medium text-slate-700">Couldn't load shipping by courier</p>
            <p className="text-xs text-slate-500 mt-1">
              This is <b>not</b> a month with no shipping — the breakdown could not be read.
            </p>
            <p className="text-xs text-red-600 mt-2 font-mono break-all">
              {shippingByCarrierError?.response?.data?.detail || shippingByCarrierError?.message || 'Unknown error'}
            </p>
          </div>
        ) : (
          <ShippingByCourierTable
            rows={shippingByCarrier}
            isLoading={shippingByCarrierLoading}
            periodLabel="Month-to-Date"
          />
        )}

      </div>

      {billingModal && (
        <BillingModal
          onClose={() => setBillingModal(false)}
          onConfirm={handleRunBilling}
          loading={billingLoading}
        />
      )}

      <ToastContainer toasts={toasts} onClose={removeToast} />
    </>
  )
}
